import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Button, Space } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import styles from './index.module.scss';

const PALETTE = [
  { color: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd', gridBg: '#faf8ff' },
  { color: '#2563eb', bg: '#eff6ff', border: '#93c5fd', gridBg: '#f5f9ff' },
  { color: '#06b6d4', bg: '#ecfeff', border: '#67e8f9', gridBg: '#f0feff' },
  { color: '#10b981', bg: '#ecfdf5', border: '#6ee7b7', gridBg: '#f0fdf9' },
  { color: '#f59e0b', bg: '#fffbeb', border: '#fcd34d', gridBg: '#fffdf0' },
  { color: '#ef4444', bg: '#fef2f2', border: '#fca5a5', gridBg: '#fff5f5' },
  { color: '#ec4899', bg: '#fdf2f8', border: '#f9a8d4', gridBg: '#fdf0f8' },
];

const LaneManager = ({ lanes, laneOrder, nodes, onUpdate, onNodesUpdate, onClose }) => {
  const { t } = useTranslation();
  const [localLanes, setLocalLanes] = useState({ ...lanes });
  const [localOrder, setLocalOrder] = useState([...laneOrder]);

  const handleRename = (key, name) => {
    setLocalLanes({
      ...localLanes,
      [key]: { ...localLanes[key], name },
    });
  };

  const handleCycleColor = (key) => {
    const current = localLanes[key].color;
    const idx = PALETTE.findIndex((p) => p.color === current);
    const next = PALETTE[(idx + 1) % PALETTE.length];
    setLocalLanes({
      ...localLanes,
      [key]: { ...localLanes[key], ...next },
    });
  };

  const handleMove = (key, direction) => {
    const idx = localOrder.indexOf(key);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= localOrder.length) return;
    const newOrder = [...localOrder];
    newOrder.splice(idx, 1);
    newOrder.splice(newIdx, 0, key);
    setLocalOrder(newOrder);
  };

  const handleDelete = (key) => {
    if (localOrder.length <= 1) {
      return;
    }
    const newOrder = localOrder.filter((k) => k !== key);
    const fallbackLane = newOrder[0];
    const newLanes = { ...localLanes };
    delete newLanes[key];
    setLocalOrder(newOrder);
    setLocalLanes(newLanes);

    if (fallbackLane && Array.isArray(nodes) && onNodesUpdate) {
      const remapped = nodes.map((node) => (node.cat === key ? { ...node, cat: fallbackLane } : node));
      onNodesUpdate(remapped);
    }
  };

  const handleAdd = () => {
    const name = prompt(t('tools.timeline.newLaneName'));
    if (!name) return;
    const key = `lane_${Date.now()}`;
    const palette = PALETTE[localOrder.length % PALETTE.length];
    setLocalLanes({
      ...localLanes,
      [key]: { name, ...palette },
    });
    setLocalOrder([...localOrder, key]);
  };

  const handleSave = () => {
    onUpdate(localLanes, localOrder);
    onClose();
  };

  return (
    <Modal
      title={t('tools.timeline.laneManagement')}
      open={true}
      onCancel={onClose}
      width={560}
      footer={[
        <Button key="add" onClick={handleAdd}>
          {t('tools.timeline.addLane')}
        </Button>,
        <Button key="close" type="primary" onClick={handleSave}>
          {t('tools.timeline.close')}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {localOrder.map((key, idx) => {
          const lane = localLanes[key];
          return (
            <div key={key} className={styles.laneRow}>
              <div
                className={styles.colorDot}
                style={{ background: lane.color, borderColor: lane.border }}
                onClick={() => handleCycleColor(key)}
                title={t('tools.timeline.clickToChangeColor')}
              />
              <Input
                value={lane.name}
                onChange={(e) => handleRename(key, e.target.value)}
                style={{ flex: 1 }}
              />
              <Button
                size="small"
                icon={<UpOutlined />}
                disabled={idx === 0}
                onClick={() => handleMove(key, -1)}
              />
              <Button
                size="small"
                icon={<DownOutlined />}
                disabled={idx === localOrder.length - 1}
                onClick={() => handleMove(key, 1)}
              />
              <Button
                size="small"
                danger
                onClick={() => handleDelete(key)}
                disabled={localOrder.length <= 1}
              >
                {t('tools.timeline.delete')}
              </Button>
            </div>
          );
        })}
      </Space>
    </Modal>
  );
};

export default LaneManager;
